// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { pb } from "@/lib/pb"
import { currentUser, logout } from "@/lib/auth"
import { getAllEntries, getExercises, getPlans, getPlanDays, getPlanDayItems, loescheVerlauf } from "@/lib/data"
import { download, exportCsv, exportJson } from "@/lib/export"
import { toast } from "@/lib/toast"
import { zufallsAlias } from "@/lib/goetter"
import { THEMES, applyTheme, currentTheme, setTrainingDunkel, trainingDunkel } from "@/lib/theme"
import { setTonAn, setVibrationAn, tonAn, vibrationAn } from "@/lib/timer"
import { FEIER_STUFEN, celebrate, feierStufe, setFeierStufe } from "@/lib/celebrate"
import { istAdmin } from "@/lib/mitglieder"
import { einfuehrungZuruecksetzen } from "@/lib/einfuehrung"
import MitgliederVerwaltung from "@/components/MitgliederVerwaltung"
import AdminUebersicht from "@/components/AdminUebersicht"

/** Was das Gerät gerade wirklich tut — beim Rätsel um die zu hohe Leiste
 *  war genau das die fehlende Information. Läuft die App im Vollbild oder im
 *  Browser? Wie hoch ist das sichtbare Fenster? Wie breit ist der Rand, den
 *  das Gerät unten für sich reserviert? */
function diagnose(): string {
  const nav = window.navigator as Navigator & { standalone?: boolean }
  const vollbild = window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true
  const rand = getComputedStyle(document.documentElement).getPropertyValue("--rand-unten").trim() || "0px"
  const sichtbar = window.visualViewport ? Math.round(window.visualViewport.height) : window.innerHeight
  const schale = Math.round(document.getElementById("root")?.getBoundingClientRect().height ?? 0)
  const leiste = document.querySelector(".bottomnav")?.getBoundingClientRect().bottom
  return [
    vollbild ? "App im Vollbild" : "im Browser",
    `Fenster ${window.innerWidth}×${window.innerHeight}`,
    `sichtbar ${sichtbar}`,
    `Bildschirm ${window.screen?.height ?? "?"}`,
    `Schale ${schale}`,
    // Sitzt die Leiste wirklich unten? Das ist die eigentliche Streitfrage.
    `Leiste endet ${leiste === undefined ? "?" : Math.round(leiste)}`,
    `Rand unten ${rand}`,
  ].join(" · ")
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const user = currentUser()
  const [name, setName] = useState(String(user?.displayName ?? ""))
  const [oldPassword, setOldPassword] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [error, setError] = useState("")
  // Feed & Ziele (Migration 1700000009: share_feed, feed_alias, weekly_goal, default_rest_sec)
  const [theme, setTheme] = useState(currentTheme())
  const [shareFeed, setShareFeed] = useState(Boolean(user?.share_feed))
  const [alias, setAlias] = useState(String(user?.feed_alias ?? ""))
  const [weeklyGoal, setWeeklyGoal] = useState(Number(user?.weekly_goal) || 0)
  const initRest = Number(user?.default_rest_sec)
  const [restSec, setRestSec] = useState(initRest > 0 ? String(initRest) : "")
  // Geräte-Einstellungen (localStorage, wie das Theme)
  const [dunkelTraining, setDunkelTraining] = useState(trainingDunkel())
  const [ton, setTon] = useState(tonAn())
  const [vibration, setVibration] = useState(vibrationAn())
  const [feier, setFeier] = useState(feierStufe())
  // Erst nach dem Aufbau messen: die untere Leiste steht in der Schale HINTER
  // dieser Seite und existiert beim ersten Durchlauf noch gar nicht.
  const [diagnoseText, setDiagnoseText] = useState("")
  useEffect(() => { setDiagnoseText(diagnose()) }, [])
  // Verlauf leeren: zweistufig, damit ein Fehlklick nicht alles mitnimmt
  const [verlaufFrage, setVerlaufFrage] = useState(false)
  const [leertGerade, setLeertGerade] = useState(false)

  /** Speichert ein Feld und hält die lokale Anmeldung frisch (wie plans.setActivePlan). */
  async function saveFeedField(patch: Record<string, unknown>) {
    if (!user) return
    try {
      const rec = await pb.collection("users").update(user.id, patch)
      pb.authStore.save(pb.authStore.token, rec)
      toast("Gespeichert ✓")
    } catch {
      toast("Speichern fehlgeschlagen – bitte erneut versuchen")
    }
  }

  function onShareChange(e: React.ChangeEvent<HTMLInputElement>) {
    const on = e.target.checked
    setShareFeed(on)
    void saveFeedField({ share_feed: on })
  }

  function saveAlias() {
    const a = alias.trim()
    setAlias(a)
    if (a === String(pb.authStore.record?.feed_alias ?? "")) return
    void saveFeedField({ feed_alias: a })
  }

  function rollAlias() {
    const a = zufallsAlias([alias])
    setAlias(a)
    void saveFeedField({ feed_alias: a })
  }

  function onGoalChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const n = Number(e.target.value)
    setWeeklyGoal(n)
    void saveFeedField({ weekly_goal: n })
  }

  function saveRest() {
    const v = Number.parseInt(restSec, 10)
    const sec = Number.isFinite(v) && v > 0 ? Math.min(v, 600) : 90
    setRestSec(String(sec))
    if (sec === Number(pb.authStore.record?.default_rest_sec)) return
    void saveFeedField({ default_rest_sec: sec })
  }

  async function saveName(e: React.FormEvent) {
    e.preventDefault()
    if (!user) return
    try {
      await pb.collection("users").update(user.id, { displayName: name.trim() })
      toast("Anzeigename gespeichert ✓")
    } catch {
      toast("Speichern fehlgeschlagen – bitte erneut versuchen")
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!user) return
    if (password !== passwordConfirm) { setError("Die neuen Passwörter stimmen nicht überein."); return }
    try {
      await pb.collection("users").update(user.id, { oldPassword, password, passwordConfirm })
      logout() // PocketBase macht alte Anmeldungen nach Passwortwechsel ungültig
      navigate("/login", { replace: true })
      toast("Passwort geändert – bitte neu anmelden")
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ""
      setError(msg.includes("schwach") ? msg : "Passwort ändern fehlgeschlagen – altes Passwort korrekt? Neues ≥10 Zeichen mit Ziffer + Sonderzeichen?")
    }
  }

  function doLogout() {
    logout()
    navigate("/login", { replace: true })
  }

  /** Erneuert serverseitig den Schlüssel — alle Ausweise dieses Kontos sind
   *  danach wertlos, auch der hier benutzte (siehe pb_hooks/abmelden.pb.js). */
  async function abmeldenUeberall() {
    try {
      await pb.send("/api/fithub/abmelden-ueberall", { method: "POST" })
      logout()
      navigate("/login", { replace: true })
      toast("Überall abgemeldet – bitte neu anmelden")
    } catch {
      toast("Dafür brauchst du Internet – bitte später erneut versuchen")
    }
  }

  // DSGVO-freundlich: ALLES, was die App über dich gespeichert hat, als eine Datei
  async function auskunft() {
    if (!user) return
    try {
      const [entries, weights, plans, days, items, posts, reactions] = await Promise.all([
        getAllEntries(),
        pb.collection("body_weights").getFullList({ sort: "date" }).catch(() => []),
        getPlans(), getPlanDays(), getPlanDayItems(),
        pb.collection("feed_posts").getFullList({ filter: `owner = "${user.id}"` }).catch(() => []),
        pb.collection("reactions").getFullList({ filter: `user = "${user.id}"` }).catch(() => []),
      ])
      const eigene = plans.filter((p) => p.owner === user.id)
      const eigeneDayIds = new Set(days.filter((d) => eigene.some((p) => p.id === d.plan)).map((d) => d.id))
      const daten = {
        erstellt: new Date().toISOString(),
        hinweis: "Alle Daten, die fitHub über dieses Konto gespeichert hat.",
        konto: {
          email: String(user.email ?? ""),
          anzeigename: String(user.displayName ?? ""),
          feed_alias: String(user.feed_alias ?? ""),
          teilt_im_feed: Boolean(user.share_feed),
          wochenziel: Number(user.weekly_goal) || 0,
          pausenzeit_sek: Number(user.default_rest_sec) || 0,
        },
        trainings_eintraege: entries,
        koerpergewicht: weights,
        eigene_plaene: eigene.map((p) => ({
          ...p,
          tage: days.filter((d) => d.plan === p.id),
          uebungen: items.filter((i) => eigeneDayIds.has(i.plan_day)),
        })),
        feed_beitraege: posts,
        reaktionen: reactions,
      }
      download("fithub-datenauskunft.json", JSON.stringify(daten, null, 2), "application/json")
      toast("Datenauskunft heruntergeladen")
    } catch {
      toast("Dafür brauchst du Internet – bitte später erneut versuchen")
    }
  }

  /** Alles wegwerfen und bei null anfangen. Vorher sichern ist der Knopf darüber. */
  async function verlaufLeeren() {
    setLeertGerade(true)
    try {
      const weg = await loescheVerlauf()
      setVerlaufFrage(false)
      toast(weg ? `${weg} Einträge gelöscht – der Verlauf ist leer` : "Es war schon nichts da")
    } catch {
      toast("Dafür brauchst du Internet – bitte später erneut versuchen")
    } finally {
      setLeertGerade(false)
    }
  }

  // Export lädt die Daten erst beim Klick — die Optionen-Seite bleibt sonst leichtgewichtig
  async function doExport(art: "csv" | "json") {
    try {
      const [entries, exs] = await Promise.all([getAllEntries(), getExercises()])
      if (!entries.length) { toast("Noch keine Einträge zum Sichern"); return }
      if (art === "csv") exportCsv(entries, new Map(exs.map((x) => [x.id, x.name])))
      else exportJson(entries)
    } catch {
      toast("Export fehlgeschlagen – bitte erneut versuchen")
    }
  }

  return (
    <>
      <h2>Anzeigename</h2>
      <form className="card" onSubmit={saveName}>
        <label htmlFor="displayName">So erscheinst du in der App</label>
        <input id="displayName" type="text" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
        <button className="save" type="submit" style={{ marginTop: 12 }}>Speichern</button>
      </form>

      <h2>Aussehen</h2>
      <div className="card themes">
        {THEMES.map((t) => (
          <button
            key={t.id}
            type="button"
            className="theme-pick"
            aria-pressed={theme === t.id}
            onClick={() => { applyTheme(t.id); setTheme(t.id) }}
          >
            <span className="theme-dot" style={{ background: t.preview[0] }}>
              <i style={{ background: t.preview[1] }} />
              <i style={{ background: t.preview[2] }} />
            </span>
            {t.name}
          </button>
        ))}
        <label className="opt-check" style={{ gridColumn: "1 / -1" }}>
          <input
            type="checkbox"
            checked={dunkelTraining}
            onChange={(e) => { setDunkelTraining(e.target.checked); setTrainingDunkel(e.target.checked) }}
          />
          Training immer dunkel – der Trainings-Tab schaltet auf „Wald bei Nacht", danach kommt dein Theme zurück
        </label>
      </div>

      <h2>Feiern</h2>
      <div className="card">
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Wie groß dürfen die Fabelwesen auftreten, wenn du etwas geschafft hast?
        </p>
        <div className="feier-wahl">
          {FEIER_STUFEN.map((s) => (
            <button
              key={s.id}
              type="button"
              className="feier-pick"
              aria-pressed={feier === s.id}
              onClick={() => {
                setFeier(s.id)
                setFeierStufe(s.id)
                // Sofort zeigen, was man gewählt hat
                celebrate({ kind: "milestone", text: `Feiern: ${s.name}` })
              }}
            >
              <b>{s.name}</b>
              <span>{s.text}</span>
            </button>
          ))}
        </div>
      </div>

      <h2>Training</h2>
      <div className="card">
        <label className="opt-check">
          <input
            type="checkbox"
            checked={ton}
            onChange={(e) => { setTon(e.target.checked); setTonAn(e.target.checked) }}
          />
          Timer-Ton (Piep bei Phasenwechsel und den letzten 3 Sekunden)
        </label>
        <label className="opt-check">
          <input
            type="checkbox"
            checked={vibration}
            onChange={(e) => { setVibration(e.target.checked); setVibrationAn(e.target.checked) }}
          />
          Vibration (wo das Gerät es kann)
        </label>

        <label htmlFor="restSec">Pausenzeit in Sekunden</label>
        <input
          id="restSec"
          type="number"
          min={10}
          max={600}
          inputMode="numeric"
          placeholder="90"
          value={restSec}
          onChange={(e) => setRestSec(e.target.value)}
          onBlur={saveRest}
        />
        <p className="hint">Pause zwischen Sätzen im geführten Training (Standard 90).</p>
      </div>

      <h2>Feed &amp; Ziele</h2>
      <div className="card">
        <label style={{ display: "flex", alignItems: "center", gap: 10, margin: 0, fontSize: 15, color: "var(--text)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={shareFeed}
            onChange={onShareChange}
            style={{ width: 20, height: 20, margin: 0, flexShrink: 0, accentColor: "var(--tanne)" }}
          />
          Meine Trainings im Feed teilen (nur Alias, nie dein Name)
        </label>

        <label htmlFor="feedAlias">Dein Götter-Alias im Feed</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            id="feedAlias"
            type="text"
            maxLength={24}
            placeholder="z. B. Thorvik"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            onBlur={saveAlias}
          />
          <button type="button" className="btn-secondary pressable" style={{ whiteSpace: "nowrap" }} onClick={rollAlias}>
            Vorschlag würfeln
          </button>
        </div>
        {shareFeed && !alias.trim() && (
          <p className="form-error" role="alert">Ohne Alias kein Feed-Beitrag. Würfle oder trag einen ein.</p>
        )}

        <label htmlFor="weeklyGoal">Wochenziel</label>
        <select id="weeklyGoal" value={weeklyGoal} onChange={onGoalChange}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
            <option key={n} value={n}>{n === 0 ? "Kein Ziel" : `${n}× pro Woche`}</option>
          ))}
        </select>
      </div>

      <h2>Passwort ändern</h2>
      <form className="card" onSubmit={changePassword}>
        <label htmlFor="oldPw">Aktuelles Passwort</label>
        <input id="oldPw" type="password" autoComplete="current-password" required value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
        <label htmlFor="newPw">Neues Passwort (≥10 Zeichen, 1 Ziffer, 1 Sonderzeichen)</label>
        <input id="newPw" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <label htmlFor="newPw2">Neues Passwort wiederholen</label>
        <input id="newPw2" type="password" autoComplete="new-password" required value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="save" type="submit" style={{ marginTop: 12 }}>Passwort ändern</button>
      </form>

      <h2>Daten &amp; Sicherung</h2>
      <div className="card">
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Deine Einträge als Datei — fürs eigene Archiv oder Excel.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary pressable" style={{ flex: 1 }} onClick={() => void doExport("csv")}>CSV exportieren</button>
          <button className="btn-secondary pressable" style={{ flex: 1 }} onClick={() => void doExport("json")}>Backup (JSON)</button>
        </div>
        <button className="btn-secondary pressable" style={{ width: "100%", marginTop: 8 }} onClick={() => void auskunft()}>
          Alle meine Daten herunterladen (Auskunft)
        </button>
        <p className="hint" style={{ margin: "8px 0 0" }}>
          Die Auskunft enthält alles, was fitHub über dich gespeichert hat — Konto, Einträge, Gewicht, eigene Pläne, Feed.
        </p>

        {/* Neu anfangen. Steht bewusst UNTER dem Export: erst sichern, dann leeren.
            Zweistufig und ohne window.confirm (wird in der App verschluckt, §13.6). */}
        {verlaufFrage ? (
          <div className="loesch-frage">
            <p>
              Deinen <b>ganzen Trainingsverlauf</b> löschen? Alle Sätze, Gewichte und Notizen
              sind dann weg, auch die Meldungen im Feed. Deine Pläne, dein Körpergewicht und
              dein Konto bleiben. Das lässt sich nicht rückgängig machen.
            </p>
            <div>
              <button type="button" className="btn-secondary" disabled={leertGerade}
                onClick={() => setVerlaufFrage(false)}>Abbrechen</button>
              <button type="button" className="loesch-frage-ja" disabled={leertGerade}
                onClick={() => void verlaufLeeren()}>
                {leertGerade ? "Löscht…" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn-secondary btn-danger pressable"
            style={{ width: "100%", marginTop: 12 }} onClick={() => setVerlaufFrage(true)}>
            Trainingsverlauf leeren
          </button>
        )}
      </div>

      <h2>Hilfe</h2>
      <div className="card">
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Die fünf Karten vom ersten Anmelden: was Plan, Training, Eintragen und Fortschritt tun.
        </p>
        <button className="btn-secondary pressable" style={{ width: "100%" }}
          onClick={() => { einfuehrungZuruecksetzen(); navigate("/start") }}>
          Einführung nochmal ansehen
        </button>
      </div>

      {istAdmin() && <><AdminUebersicht /><MitgliederVerwaltung /></>}

      <h2>Konto</h2>
      <div className="card">
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Angemeldet als {String(user?.username || user?.email || user?.displayName || "")}
        </p>
        <button className="btn-secondary btn-danger" style={{ width: "100%" }} onClick={doLogout}>Abmelden</button>
        <button className="btn-secondary btn-danger pressable" style={{ width: "100%", marginTop: 8 }}
          onClick={() => void abmeldenUeberall()}>
          Auf allen Geräten abmelden
        </button>
        <p className="hint" style={{ margin: "8px 0 0" }}>
          Handy verloren? Damit werden alle Anmeldungen sofort ungültig – auch diese hier.
          Danach überall einmal neu anmelden.
        </p>
      </div>

      <h2>Diagnose</h2>
      <div className="card">
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Wenn etwas komisch aussieht: diese Zeile abfotografieren und schicken.
        </p>
        <p className="diagnose">{diagnoseText}</p>
      </div>

      <footer>
        Übungs-Animationen © <a href="https://gymvisual.com/" target="_blank" rel="noreferrer">Gymvisual</a>,
        via exercises-dataset (MIT) · fitHub ist kein medizinischer Rat.
      </footer>
    </>
  )
}
